/* ─── Scribble: drawing surface overlaid on a nested component ─── */

import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent, type ReactNode,
} from 'react';

import { useArtifactSendEvent } from '../store';
import { useValueClock, type VectorClock } from '../useValueClock';
import { COLOR_MAP, darken, luminance, parseColor } from '../colors';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';

// ─── Shape wire types (mirror artifact/data/d_scribble.py) ──────────────

type Pixel = [number, number];

interface Mark   { type: 'mark';   pos: Pixel; color?: string | null; label?: string | null; }
interface Stroke { type: 'stroke'; path: Pixel[]; color?: string | null; label?: string | null; }
interface Line   { type: 'line';   start: Pixel; end: Pixel; color?: string | null; label?: string | null; }
interface Arrow  { type: 'arrow';  start: Pixel; end: Pixel; color?: string | null; label?: string | null; }
interface Box    { type: 'box';    tl: Pixel; br: Pixel; color?: string | null; label?: string | null; }
interface CircleShape { type: 'circle'; pos: Pixel; r: number; color?: string | null; label?: string | null; }
interface TextShape { type: 'text'; pos: Pixel; text: string; color?: string | null; label?: string | null; }

type Shape = Mark | Stroke | Line | Arrow | Box | CircleShape | TextShape;

type Tool = 'mark' | 'stroke' | 'line' | 'arrow' | 'box' | 'circle' | 'text' | 'eraser';

// Order matches the toolbar layout.
const ALL_TOOLS: Tool[] = ['mark', 'stroke', 'line', 'arrow', 'box', 'circle', 'text', 'eraser'];
const MINIMAL_TOOLS: Tool[] = ['line', 'text', 'eraser'];

// Themed color names the user can pick from. Mirror of `d_color.ThemeColor`
// — these are the only names the picker is allowed to surface, since the
// server's `ScribbleColorChanged` event is typed `tool: ThemeColor`.
type ThemeColor =
  | 'black' | 'white' | 'gray'
  | 'red' | 'green' | 'blue'
  | 'orange' | 'yellow' | 'purple' | 'pink' | 'teal' | 'brown'
  | 'periwinkle';

// Order chosen for the popup grid (browns/grays last).
const ALL_COLORS: ThemeColor[] = [
  'red', 'orange', 'yellow', 'green', 'teal', 'blue',
  'periwinkle', 'purple', 'pink', 'brown', 'black', 'gray', 'white',
];
const THEME_COLOR_SET: ReadonlySet<string> = new Set(ALL_COLORS);

// ─── Drawing primitives ────────────────────────────────────────────────

const TOOL_ICON: Record<Tool, ReactNode> = {
  mark: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <line x1="7" y1="7" x2="13" y2="13" />
      <line x1="13" y1="7" x2="7" y2="13" />
    </svg>
  ),
  stroke: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M3 12 C 6 4, 9 18, 12 10 S 17 8, 17 8" />
    </svg>
  ),
  line: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <line x1="4" y1="15" x2="15" y2="5" />
    </svg>
  ),
  arrow: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="15" x2="15" y2="5" />
      <polyline points="10,5 15,5 15,10" />
    </svg>
  ),
  box: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinejoin="round">
      <rect x="3" y="5" width="14" height="10" rx="1.5" />
    </svg>
  ),
  circle: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2}>
      <circle cx="10" cy="10" r="5.5" />
    </svg>
  ),
  text: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 5 H15" />
      <path d="M10 5 V16" />
      <path d="M8 16 H12" />
    </svg>
  ),
  eraser: (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none"
         stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 14 L4 11 L11 4 L16 9 L11 14 Z" />
      <path d="M9 6 L14 11" />
      <path d="M5 16 H15" />
    </svg>
  ),
};

// ─── Hit testing & translation ──────────────────────────────────────────

const HIT_TOL = 8;
const TEXT_FONT_SIZE = 16;
const TEXT_LINE_HEIGHT = 20;
const TEXT_FONT = `${TEXT_FONT_SIZE}px ui-sans-serif, system-ui, sans-serif`;

function _dist(a: Pixel, b: Pixel): number {
  const dx = a[0] - b[0]; const dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
}

function _distToSegment(p: Pixel, a: Pixel, b: Pixel): number {
  const vx = b[0] - a[0]; const vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  if (len2 === 0) return _dist(p, a);
  let t = ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  return _dist(p, [a[0] + t * vx, a[1] + t * vy]);
}

function hitTest(s: Shape, pt: Pixel, tol: number = HIT_TOL): boolean {
  switch (s.type) {
    case 'mark':   return _dist(pt, s.pos) <= tol + 4;
    case 'stroke':
      if (s.path.length === 1) return _dist(pt, s.path[0]) <= tol;
      for (let i = 0; i + 1 < s.path.length; i++) {
        if (_distToSegment(pt, s.path[i], s.path[i + 1]) <= tol) return true;
      }
      return false;
    case 'line':
    case 'arrow':  return _distToSegment(pt, s.start, s.end) <= tol;
    case 'box': {
      const x0 = Math.min(s.tl[0], s.br[0]);
      const y0 = Math.min(s.tl[1], s.br[1]);
      const x1 = Math.max(s.tl[0], s.br[0]);
      const y1 = Math.max(s.tl[1], s.br[1]);
      return Math.min(
        _distToSegment(pt, [x0, y0], [x1, y0]),
        _distToSegment(pt, [x1, y0], [x1, y1]),
        _distToSegment(pt, [x1, y1], [x0, y1]),
        _distToSegment(pt, [x0, y1], [x0, y0]),
      ) <= tol;
    }
    case 'circle': return Math.abs(_dist(pt, s.pos) - s.r) <= tol;
    case 'text': {
      const bounds = textBounds(s);
      return pt[0] >= bounds.x - tol &&
        pt[0] <= bounds.x + bounds.w + tol &&
        pt[1] >= bounds.y - tol &&
        pt[1] <= bounds.y + bounds.h + tol;
    }
  }
}

function hitInside(s: Shape, pt: Pixel): boolean {
  // Only the closed shapes have a meaningful interior.
  switch (s.type) {
    case 'box': {
      const x0 = Math.min(s.tl[0], s.br[0]);
      const y0 = Math.min(s.tl[1], s.br[1]);
      const x1 = Math.max(s.tl[0], s.br[0]);
      const y1 = Math.max(s.tl[1], s.br[1]);
      return pt[0] >= x0 && pt[0] <= x1 && pt[1] >= y0 && pt[1] <= y1;
    }
    case 'circle': return _dist(pt, s.pos) <= s.r;
    case 'text': return hitTest(s, pt, 0);
    default: return false;
  }
}

function textBounds(s: TextShape): { x: number; y: number; w: number; h: number } {
  const lines = s.text.split('\n');
  const maxChars = Math.max(1, ...lines.map((line) => line.length));
  return {
    x: s.pos[0],
    y: s.pos[1],
    w: Math.max(24, maxChars * 8.5),
    h: Math.max(TEXT_LINE_HEIGHT, lines.length * TEXT_LINE_HEIGHT),
  };
}

function shapeAnchor(s: Shape): Pixel {
  // Returns the *centre point* the label badge should be drawn at —
  // tuned per-shape so the letter sits in the most natural spot
  // (e.g. just inside the top edge of a box, above a circle, off the
  // top-left of a mark, etc.).
  const inset = 10;
  switch (s.type) {
    case 'mark':   return [s.pos[0] + inset, s.pos[1] + inset];
    case 'circle': return [s.pos[0], s.pos[1] - s.r + inset];
    case 'line':
    case 'arrow':  return [(s.start[0] + s.end[0]) / 2, (s.start[1] + s.end[1]) / 2];
    case 'box': {
      const left = Math.min(s.tl[0], s.br[0]);
      const top = Math.min(s.tl[1], s.br[1]);
      return [left + inset, top + inset];
    }
    case 'stroke': {
      const pts = s.path;
      if (!pts || pts.length === 0) return [0, 0];
      return pts[Math.floor(pts.length / 2)];
    }
    case 'text': return [s.pos[0] + 10, s.pos[1] + 10];
  }
}

function drawBadge(ctx: CanvasRenderingContext2D, pt: Pixel, letter: string) {
  // No fill background — draw the letter with a thick black stroke
  // underneath so it stays legible regardless of what's behind it.
  // `pt` is the desired centre of the glyph.
  const cx = pt[0];
  const cy = pt[1];
  const prevFill = ctx.fillStyle;
  const prevStroke = ctx.strokeStyle;
  const prevLineWidth = ctx.lineWidth;
  const prevLineJoin = ctx.lineJoin;
  const prevFont = ctx.font;
  const prevAlign = ctx.textAlign;
  const prevBaseline = ctx.textBaseline;
  ctx.font = '700 13px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#000';
  ctx.strokeText(letter, cx, cy + 0.5);
  ctx.fillStyle = '#FFF';
  ctx.fillText(letter, cx, cy + 0.5);
  ctx.fillStyle = prevFill;
  ctx.strokeStyle = prevStroke;
  ctx.lineWidth = prevLineWidth;
  ctx.lineJoin = prevLineJoin;
  ctx.font = prevFont;
  ctx.textAlign = prevAlign;
  ctx.textBaseline = prevBaseline;
}

function shiftShape(s: Shape, dx: number, dy: number): Shape {
  switch (s.type) {
    case 'mark':   return { ...s, pos: [s.pos[0] + dx, s.pos[1] + dy] };
    case 'stroke': return { ...s, path: s.path.map(([x, y]) => [x + dx, y + dy] as Pixel) };
    case 'line':
    case 'arrow':  return { ...s, start: [s.start[0] + dx, s.start[1] + dy], end: [s.end[0] + dx, s.end[1] + dy] };
    case 'box':    return { ...s, tl: [s.tl[0] + dx, s.tl[1] + dy], br: [s.br[0] + dx, s.br[1] + dy] };
    case 'circle': return { ...s, pos: [s.pos[0] + dx, s.pos[1] + dy] };
    case 'text':   return { ...s, pos: [s.pos[0] + dx, s.pos[1] + dy] };
  }
}

// ─── Drawing ────────────────────────────────────────────────────────────

function drawShape(ctx: CanvasRenderingContext2D, shape: Shape, fallbackColor: string) {
  // Per-shape color override; falls through to the `color` prop.
  const c = parseColor(shape.color ?? fallbackColor);
  ctx.strokeStyle = c;
  ctx.fillStyle = c;
  switch (shape.type) {
    case 'mark': {
      const [x, y] = shape.pos;
      const r = 4;
      ctx.beginPath();
      ctx.moveTo(x - r, y - r); ctx.lineTo(x + r, y + r);
      ctx.moveTo(x + r, y - r); ctx.lineTo(x - r, y + r);
      ctx.stroke();
      return;
    }
    case 'stroke': {
      const pts = shape.path;
      if (!pts || pts.length === 0) return;
      if (pts.length === 1) {
        const r = Math.max(2, ctx.lineWidth + 0.5);
        ctx.beginPath();
        ctx.arc(pts[0][0], pts[0][1], r, 0, Math.PI * 2);
        ctx.fill();
        return;
      }
      if (pts.length === 2) {
        ctx.beginPath();
        ctx.moveTo(pts[0][0], pts[0][1]);
        ctx.lineTo(pts[1][0], pts[1][1]);
        ctx.stroke();
        return;
      }
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length - 1; i++) {
        const mx = (pts[i][0] + pts[i + 1][0]) / 2;
        const my = (pts[i][1] + pts[i + 1][1]) / 2;
        ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
      }
      const last = pts[pts.length - 1];
      const prev = pts[pts.length - 2];
      ctx.quadraticCurveTo(prev[0], prev[1], last[0], last[1]);
      ctx.stroke();
      return;
    }
    case 'line': {
      ctx.beginPath();
      ctx.moveTo(shape.start[0], shape.start[1]);
      ctx.lineTo(shape.end[0], shape.end[1]);
      ctx.stroke();
      return;
    }
    case 'arrow': {
      const [sx, sy] = shape.start;
      const [ex, ey] = shape.end;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(ex, ey);
      ctx.stroke();
      // Arrowhead — two short strokes at the tip.
      const len = _dist(shape.start, shape.end);
      if (len < 1) return;
      const ux = (ex - sx) / len; const uy = (ey - sy) / len;
      const head = Math.max(8, ctx.lineWidth * 4);
      const angle = Math.PI / 6;
      const cosA = Math.cos(angle); const sinA = Math.sin(angle);
      // Rotate -u by ±angle.
      const lx = ex - head * (ux * cosA - uy * sinA);
      const ly = ey - head * (uy * cosA + ux * sinA);
      const rx = ex - head * (ux * cosA + uy * sinA);
      const ry = ey - head * (uy * cosA - ux * sinA);
      ctx.beginPath();
      ctx.moveTo(lx, ly); ctx.lineTo(ex, ey); ctx.lineTo(rx, ry);
      ctx.stroke();
      return;
    }
    case 'box': {
      const x = Math.min(shape.tl[0], shape.br[0]);
      const y = Math.min(shape.tl[1], shape.br[1]);
      const w = Math.abs(shape.br[0] - shape.tl[0]);
      const h = Math.abs(shape.br[1] - shape.tl[1]);
      ctx.strokeRect(x, y, w, h);
      return;
    }
    case 'circle': {
      ctx.beginPath();
      ctx.arc(shape.pos[0], shape.pos[1], shape.r, 0, Math.PI * 2);
      ctx.stroke();
      return;
    }
    case 'text': {
      ctx.font = TEXT_FONT;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const lines = shape.text.split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, shape.pos[0], shape.pos[1] + i * TEXT_LINE_HEIGHT);
      });
      return;
    }
  }
}

// ─── Component ──────────────────────────────────────────────────────────

type Draft =
  | { tool: 'stroke'; points: Pixel[] }
  | { tool: 'mark' | 'line' | 'arrow' | 'box' | 'circle'; start: Pixel; end: Pixel };

interface TextEditorState {
  pos: Pixel;
  value: string;
}

type MenuSpec<T extends string> = T[] | 'all' | 'none';
type ToolMenuSpec = MenuSpec<Tool> | 'minimal';

function resolveMenu<T extends string>(spec: MenuSpec<T> | undefined, all: T[]): T[] | null {
  if (spec === undefined || spec === 'all') return all;
  if (spec === 'none') return null;
  if (Array.isArray(spec)) return spec.length ? spec : null;
  return all;
}

function resolveToolMenu(spec: ToolMenuSpec | undefined): Tool[] | null {
  if (spec === 'minimal') return MINIMAL_TOOLS;
  return resolveMenu(spec, ALL_TOOLS);
}

type ScribbleValue = {
  shapes?: Shape[];
  tool?: Tool;
  color?: string;
  // When this is a dict (i.e. not null/undefined), the per-shape comments
  // table renders below the canvas and new shapes are auto-labelled. Keys
  // are shape labels (e.g. 'a', 'b'); values are user-typed comment text.
  comments?: Record<string, string> | null;
};

// Auto-label allocator: returns the first unused single-letter label for
// the given shapes. Iterates 'a'..'z' (sufficient for a small annotation
// surface) and falls back to 'a' once exhausted.
function nextFreeLabel(shapes: Shape[]): string {
  const used = new Set<string>();
  for (const s of shapes) if (s.label) used.add(s.label);
  for (let i = 0; i < 26; i++) {
    const ch = String.fromCharCode(97 + i);
    if (!used.has(ch)) return ch;
  }
  return 'a';
}

export function ArtifactScribble({
  content,
  value: valueProp,
  bg_shapes,
  thickness: thicknessProp,
  tool_menu,
  color_menu,
  show_done,
  __node_id,
  __clock,
}: {
  content?: ReactNode;
  value?: ScribbleValue;
  bg_shapes?: Shape[];
  thickness?: number;
  tool_menu?: ToolMenuSpec;
  color_menu?: MenuSpec<ThemeColor>;
  show_done?: boolean;
  __node_id?: string;
  __clock?: VectorClock;
}) {
  const shapesProp = valueProp?.shapes;
  const toolProp = valueProp?.tool;
  const colorProp = valueProp?.color;
  const commentsProp = valueProp?.comments;
  const sendEvent = useArtifactSendEvent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textEditorRef = useRef<HTMLTextAreaElement>(null);
  const draftRef = useRef<Draft | null>(null);
  // Active shift-drag of an existing user shape. `index` is into the
  // client-authoritative `shapes` array.
  const dragRef = useRef<{ index: number; dx: number; dy: number; start: Pixel } | null>(null);
  const erasingRef = useRef(false);

  const [tool, setTool] = useState<Tool>(toolProp ?? 'stroke');
  const [color, setColor] = useState<string>(colorProp ?? 'red');
  const [shapes, setShapes] = useState<Shape[]>(
    Array.isArray(shapesProp) ? shapesProp : [],
  );
  const [textEditor, setTextEditor] = useState<TextEditorState | null>(null);
  // `null` means the comments feature is disabled (the agent didn't
  // pass `enable_comments=True`); a dict (possibly empty) means it's on.
  const [comments, setComments] = useState<Record<string, string> | null>(
    commentsProp === undefined ? null : commentsProp,
  );
  const commentsEnabled = comments !== null;

  const thickness = thicknessProp ?? 2;

  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [intrinsic, setIntrinsic] = useState<{ w: number; h: number } | null>(null);

  const clock = useValueClock(__clock);
  useEffect(() => {
    if (!clock.isFresh()) return;
    clock.accept();
    if (valueProp?.shapes !== undefined) setShapes(valueProp.shapes);
    if (valueProp?.tool !== undefined) setTool(valueProp.tool);
    if (valueProp?.color !== undefined) setColor(valueProp.color);
    if (valueProp?.comments !== undefined) setComments(valueProp.comments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [__clock, valueProp]);

  // Hover tracking — used to scope keyboard shortcuts to "the scribble
  // the cursor is over".
  const [hovered, setHovered] = useState(false);
  // Whether the user is currently holding shift — drives the grab cursor
  // and the "pan existing shape" interaction in `handlePointerDown`.
  const [shiftDown, setShiftDown] = useState(false);
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftDown(true); };
    const onUp = (e: KeyboardEvent) => { if (e.key === 'Shift') setShiftDown(false); };
    const onBlur = () => setShiftDown(false);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  const bgShapes: Shape[] = Array.isArray(bg_shapes) ? bg_shapes : [];

  // 'all' / 'none' / explicit list — resolve once on the client.
  // When comments are enabled the component is in "point and comment" mode;
  // the text tool doesn't belong there (the comment inputs below the canvas
  // are the text entry mechanism).
  const rawMenuTools = resolveToolMenu(tool_menu);
  const menuTools = commentsEnabled && rawMenuTools
    ? rawMenuTools.filter((t) => t !== 'text')
    : rawMenuTools;
  // Defensive filter — drop anything that isn't a known theme color so
  // a stray hex / unknown name from the server doesn't end up as a
  // selectable swatch (the server-side `ScribbleColorChanged.color` is
  // typed `ThemeColor`, so a hex would fail to round-trip anyway).
  const rawMenuColors = resolveMenu(color_menu, ALL_COLORS);
  const menuColors: ThemeColor[] | null = rawMenuColors === null
    ? null
    : rawMenuColors.filter((c): c is ThemeColor => THEME_COLOR_SET.has(c));

  // Whether to stamp `color` onto added/moved shapes. When the color
  // menu is suppressed the user has no per-shape control, so we don't
  // serialize a redundant per-shape color.
  const shapesCarryColor = menuColors !== null;

  // Refs to the latest values of every field that goes into the
  // `ScribbleChanged` snapshot — the debounced sender reads from here so
  // it can't pick up stale state from a closure captured during an
  // earlier render.
  const shapesRef = useRef(shapes);
  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  const commentsRef = useRef(comments);
  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  useEffect(() => { toolRef.current = tool; }, [tool]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { commentsRef.current = comments; }, [comments]);

  // Per-field dirty flags. `ScribbleValue` is partial — we only ship the
  // fields that actually changed since the last flush, so a server
  // callback can detect *what* changed via `event.color is not None` etc.
  const dirtyShapesRef = useRef(false);
  const dirtyToolRef = useRef(false);
  const dirtyColorRef = useRef(false);
  const dirtyCommentsRef = useRef(false);

  const changedTimerRef = useRef<number | null>(null);
  const flushChanged = () => {
    if (!__node_id) return;
    if (
      !dirtyShapesRef.current &&
      !dirtyToolRef.current &&
      !dirtyColorRef.current &&
      !dirtyCommentsRef.current
    ) {
      return;
    }
    const value: ScribbleValue = {};
    if (dirtyShapesRef.current)   value.shapes   = shapesRef.current;
    if (dirtyToolRef.current)     value.tool     = toolRef.current;
    if (dirtyColorRef.current)    value.color    = colorRef.current as ThemeColor;
    if (dirtyCommentsRef.current) value.comments = commentsRef.current;
    dirtyShapesRef.current = false;
    dirtyToolRef.current = false;
    dirtyColorRef.current = false;
    dirtyCommentsRef.current = false;
    sendEvent(__node_id, 'changed', {
      kind: 'ScribbleChanged',
      value,
      _clock: clock.bump(),
    });
  };
  const scheduleChanged = () => {
    if (!__node_id) return;
    if (changedTimerRef.current !== null) window.clearTimeout(changedTimerRef.current);
    changedTimerRef.current = window.setTimeout(() => {
      changedTimerRef.current = null;
      flushChanged();
    }, 250);
  };
  useEffect(() => () => {
    if (changedTimerRef.current !== null) window.clearTimeout(changedTimerRef.current);
  }, []);

  useLayoutEffect(() => {
    if (!textEditor) return;
    textEditorRef.current?.focus();
  }, [textEditor]);

  const handleSelectTool = (t: Tool) => {
    setTool(t);
    toolRef.current = t;
    dirtyToolRef.current = true;
    scheduleChanged();
  };

  const handleSelectColor = (c: ThemeColor) => {
    setColor(c);
    colorRef.current = c;
    dirtyColorRef.current = true;
    scheduleChanged();
    setColorPickerOpen(false);
  };

  const handleClear = () => {
    setTextEditor(null);
    setShapes([]);
    shapesRef.current = [];
    dirtyShapesRef.current = true;
    if (changedTimerRef.current !== null) {
      window.clearTimeout(changedTimerRef.current);
      changedTimerRef.current = null;
    }
    flushChanged();
    if (__node_id) {
      sendEvent(__node_id, 'cleared', {
        kind: 'ScribbleCleared',
        _clock: clock.current(),
      });
    }
  };

  const handleDone = () => {
    if (!__node_id) return;
    // Flush any pending debounced change first so the server's snapshot
    // reflects the user's state at the moment they clicked done.
    if (changedTimerRef.current !== null) {
      window.clearTimeout(changedTimerRef.current);
      changedTimerRef.current = null;
    }
    flushChanged();
    const marks: Mark[] = [];
    const strokes: Stroke[] = [];
    const lines: Line[] = [];
    const arrows: Arrow[] = [];
    const boxes: Box[] = [];
    const circles: CircleShape[] = [];
    const texts: TextShape[] = [];
    for (const s of shapesRef.current) {
      switch (s.type) {
        case 'mark':   marks.push(s); break;
        case 'stroke': strokes.push(s); break;
        case 'line':   lines.push(s); break;
        case 'arrow':  arrows.push(s); break;
        case 'box':    boxes.push(s); break;
        case 'circle': circles.push(s); break;
        case 'text':   texts.push(s); break;
      }
    }
    sendEvent(__node_id, 'done', {
      kind: 'ScribbleDone',
      marks, strokes, lines, arrows, boxes, circles, texts,
      _clock: clock.current(),
    });
  };

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ w: Math.round(width), h: Math.round(height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const img = el.querySelector('img');
    if (!img) { setIntrinsic(null); return; }
    const read = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setIntrinsic({ w: img.naturalWidth, h: img.naturalHeight });
      }
    };
    read();
    img.addEventListener('load', read);
    return () => img.removeEventListener('load', read);
  }, [content]);

  // Keyboard shortcuts — scoped to the wrapper via hover.
  useEffect(() => {
    if (!hovered) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      const k = e.key.toLowerCase();
      // In text mode, plain letter keys are expected to become text, not
      // tool-switch shortcuts. The editor itself is filtered above; this
      // also protects the pre-placement state while the text tool is active.
      if (toolRef.current === 'text') {
        if (k === 'd') { handleDone(); e.preventDefault(); }
        return;
      }
      const map: Record<string, Tool> = {
        x: 'mark',
        s: 'stroke',
        l: 'line',
        a: 'arrow',
        b: 'box',
        o: 'circle',
        t: 'text',
        e: 'eraser',
      };
      if (k in map) {
        const next = map[k];
        if (!menuTools || menuTools.includes(next)) {
          handleSelectTool(next);
          e.preventDefault();
        }
        return;
      }
      if (k === 'c') { handleClear(); e.preventDefault(); return; }
      if (k === 'd') { handleDone(); e.preventDefault(); return; }
      if (e.key === 'Backspace' || e.key === 'Delete') {
        // Remove the most-recently-added shape, plus its comment entry
        // if it had a label.
        const last = shapesRef.current[shapesRef.current.length - 1];
        if (!last) return;
        e.preventDefault();
        setShapes((prev) => {
          const next = prev.slice(0, -1);
          shapesRef.current = next;
          return next;
        });
        dirtyShapesRef.current = true;
        if (last.label && commentsRef.current && last.label in commentsRef.current) {
          setComments((prev) => {
            if (!prev) return prev;
            const next = { ...prev };
            delete next[last.label as string];
            commentsRef.current = next;
            return next;
          });
          dirtyCommentsRef.current = true;
        }
        scheduleChanged();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, menuTools]);

  const canvasW = intrinsic?.w ?? size.w;
  const canvasH = intrinsic?.h ?? size.h;

  const draftShape = useCallback((d: Draft): Shape | null => {
    const stamp = shapesCarryColor ? color : null;
    switch (d.tool) {
      case 'mark':   return { type: 'mark',   pos: d.end, color: stamp };
      case 'stroke': return d.points.length === 0 ? null : { type: 'stroke', path: d.points, color: stamp };
      case 'line':   return { type: 'line',   start: d.start, end: d.end, color: stamp };
      case 'arrow':  return { type: 'arrow',  start: d.start, end: d.end, color: stamp };
      case 'box': {
        // Normalize so `.tl` is truly top-left and `.br` truly bottom-right,
        // regardless of drag direction — the server-side schema declares these
        // fields with that meaning.
        const tl: Pixel = [Math.min(d.start[0], d.end[0]), Math.min(d.start[1], d.end[1])];
        const br: Pixel = [Math.max(d.start[0], d.end[0]), Math.max(d.start[1], d.end[1])];
        return { type: 'box', tl, br, color: stamp };
      }
      case 'circle': {
        const dx = d.end[0] - d.start[0]; const dy = d.end[1] - d.start[1];
        return { type: 'circle', pos: d.start, r: Math.round(Math.sqrt(dx * dx + dy * dy)), color: stamp };
      }
    }
  }, [color, shapesCarryColor]);

  const drawAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.lineWidth = thickness;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    for (const s of bgShapes) drawShape(ctx, s, color);

    const drag = dragRef.current;
    for (let i = 0; i < shapes.length; i++) {
      const original = shapes[i];
      const s = (drag && drag.index === i)
        ? shiftShape(original, drag.dx, drag.dy)
        : original;
      drawShape(ctx, s, color);
      if (s.label) {
        drawBadge(ctx, shapeAnchor(s), s.label.toUpperCase());
      }
    }

    const d = draftRef.current;
    if (d) {
      const shape = draftShape(d);
      if (shape) drawShape(ctx, shape, color);
    }
  }, [bgShapes, shapes, color, thickness, draftShape]);

  useEffect(() => { drawAll(); }, [drawAll, size, intrinsic]);

  const pointFromEvent = (e: ReactPointerEvent<HTMLCanvasElement>): Pixel => {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width > 0 ? canvas.width / rect.width : 1;
    const sy = rect.height > 0 ? canvas.height / rect.height : 1;
    return [
      Math.round((e.clientX - rect.left) * sx),
      Math.round((e.clientY - rect.top) * sy),
    ];
  };

  const commitTextEditor = (editor = textEditor) => {
    if (!editor) return;
    setTextEditor(null);
    const text = editor.value.trim();
    if (!text) return;

    const shape: TextShape = {
      type: 'text',
      pos: editor.pos,
      text,
      color: shapesCarryColor ? color : null,
    };
    setShapes((prev) => {
      const next = [...prev, shape];
      shapesRef.current = next;
      return next;
    });
    dirtyShapesRef.current = true;
    scheduleChanged();
  };

  const eraseAtPoint = (pt: Pixel): boolean => {
    const current = shapesRef.current;
    for (let i = current.length - 1; i >= 0; i--) {
      const shape = current[i];
      if (!hitTest(shape, pt) && !hitInside(shape, pt)) continue;

      const next = [...current.slice(0, i), ...current.slice(i + 1)];
      shapesRef.current = next;
      setShapes(next);
      dirtyShapesRef.current = true;

      if (shape.label && commentsRef.current && shape.label in commentsRef.current) {
        const nextComments = { ...commentsRef.current };
        delete nextComments[shape.label];
        commentsRef.current = nextComments;
        setComments(nextComments);
        dirtyCommentsRef.current = true;
      }

      scheduleChanged();
      return true;
    }
    return false;
  };

  const handlePointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const pt = pointFromEvent(e);
    if (tool === 'text') {
      if (textEditor) commitTextEditor(textEditor);
      setTextEditor({ pos: pt, value: '' });
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      e.preventDefault();
      return;
    }
    if (tool === 'eraser') {
      erasingRef.current = true;
      eraseAtPoint(pt);
      return;
    }
    if (e.shiftKey) {
      // Shift = "pan an existing shape". Try perimeter then interior; if
      // neither hits, do nothing — never start a new draft when shift is
      // held.
      for (let i = shapes.length - 1; i >= 0; i--) {
        if (hitTest(shapes[i], pt)) {
          dragRef.current = { index: i, dx: 0, dy: 0, start: pt };
          drawAll();
          return;
        }
      }
      for (let i = shapes.length - 1; i >= 0; i--) {
        if (hitInside(shapes[i], pt)) {
          dragRef.current = { index: i, dx: 0, dy: 0, start: pt };
          drawAll();
          return;
        }
      }
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      return;
    }
    draftRef.current = tool === 'stroke'
      ? { tool: 'stroke', points: [pt] }
      : { tool, start: pt, end: pt };
    drawAll();
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (erasingRef.current) {
      eraseAtPoint(pointFromEvent(e));
      return;
    }
    const drag = dragRef.current;
    if (drag) {
      const pt = pointFromEvent(e);
      drag.dx = pt[0] - drag.start[0];
      drag.dy = pt[1] - drag.start[1];
      drawAll();
      return;
    }
    const d = draftRef.current;
    if (!d) return;
    const pt = pointFromEvent(e);
    if (d.tool === 'stroke') {
      const prev = d.points[d.points.length - 1];
      const dx = pt[0] - prev[0]; const dy = pt[1] - prev[1];
      if (dx * dx + dy * dy < 100) return;
      d.points.push(pt);
    } else {
      d.end = pt;
    }
    drawAll();
  };

  const handlePointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (erasingRef.current) {
      erasingRef.current = false;
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      return;
    }
    const drag = dragRef.current;
    if (drag) {
      dragRef.current = null;
      if (drag.dx !== 0 || drag.dy !== 0) {
        setShapes((prev) => {
          const next = [...prev];
          next[drag.index] = shiftShape(next[drag.index], drag.dx, drag.dy);
          shapesRef.current = next;
          return next;
        });
        dirtyShapesRef.current = true;
        scheduleChanged();
      }
      drawAll();
      try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      return;
    }
    const d = draftRef.current;
    draftRef.current = null;
    if (!d) return;
    const tail = pointFromEvent(e);
    if (d.tool === 'stroke') {
      const last = d.points[d.points.length - 1];
      if (tail[0] !== last[0] || tail[1] !== last[1]) d.points.push(tail);
    } else {
      d.end = tail;
    }

    // A click-without-drag only registers when the `mark` tool is active.
    const TAP_RADIUS_SQ = 25;
    const start: Pixel = d.tool === 'stroke' ? d.points[0] : d.start;
    const dx = tail[0] - start[0]; const dy = tail[1] - start[1];
    const isTap = d.tool === 'stroke'
      ? d.points.length <= 2 && dx * dx + dy * dy < TAP_RADIUS_SQ
      : dx * dx + dy * dy < TAP_RADIUS_SQ;

    let shape: Shape | null;
    if (isTap) {
      shape = d.tool === 'mark'
        ? { type: 'mark', pos: tail, color: shapesCarryColor ? color : null }
        : null;
    } else {
      shape = draftShape(d);
    }
    if (shape) {
      let committed = shape;
      setShapes((prev) => {
        // When the comments table is enabled, every new shape gets the
        // first free single-letter label so it can be addressed in the
        // table below.
        if (commentsRef.current !== null && !committed.label) {
          committed = { ...committed, label: nextFreeLabel(prev) };
        }
        const next = [...prev, committed];
        shapesRef.current = next;
        return next;
      });
      dirtyShapesRef.current = true;
      scheduleChanged();
    }
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  // Menu styling
  const userColor = parseColor(color);
  const isLightMenuBg = luminance(userColor) < 0.4;
  const menuBg = isLightMenuBg
    ? 'var(--artifact-foreground, #FFFDF9)'
    : 'var(--artifact-background, #222222)';
  const menuBorder = isLightMenuBg
    ? '1px solid rgba(0,0,0,0.12)'
    : '1px solid var(--artifact-border, #323232)';
  const menuFg = isLightMenuBg
    ? 'var(--artifact-background, #222222)'
    : 'var(--artifact-foreground, #FFFDF9)';

  // Hint verb (when tool_menu is none).
  const [coarsePointer, setCoarsePointer] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(pointer: coarse)');
    const onChange = () => setCoarsePointer(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const tapOrClick = coarsePointer ? 'tap' : 'click';
  const hintVerb = tool === 'mark' ? tapOrClick : 'drag';

  const TOOL_TIP: Record<Tool, string> = {
    mark:   '_x mark',
    stroke: '_s stroke',
    line:   '_l line',
    arrow:  '_a arrow',
    box:    '_b box',
    circle: '_o circle',
    text:   '_t text',
    eraser: '_e eraser',
  };

  function renderTip(spec: string): ReactNode {
    const m = /^_(.) (.+)$/.exec(spec);
    if (!m) return spec;
    const [, key, rest] = m;
    const lower = key.toLowerCase();
    const idx = rest.toLowerCase().indexOf(lower);
    if (idx < 0) return <><u>{key}</u> {rest}</>;
    return (<>{rest.slice(0, idx)}<u>{rest[idx]}</u>{rest.slice(idx + 1)}</>);
  }

  // Color picker popover state.
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!colorPickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target as Node)) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [colorPickerOpen]);

  const textEditorStyle: CSSProperties | undefined = textEditor ? {
    position: 'absolute',
    zIndex: 2,
    left: canvasW > 0 ? `${(textEditor.pos[0] / canvasW) * 100}%` : textEditor.pos[0],
    top: canvasH > 0 ? `${(textEditor.pos[1] / canvasH) * 100}%` : textEditor.pos[1],
    minWidth: 140,
    minHeight: TEXT_LINE_HEIGHT + 8,
    padding: '3px 5px',
    border: `1px solid ${userColor}`,
    borderRadius: 4,
    outline: 'none',
    resize: 'both',
    background: 'rgba(34, 34, 34, 0.82)',
    color: userColor,
    font: TEXT_FONT,
    lineHeight: `${TEXT_LINE_HEIGHT}px`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
  } : undefined;

  return (
    <div
      className="a-scribble-outer"
      style={{ display: 'inline-block', maxWidth: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        role="toolbar"
        aria-label="Scribble tools"
        style={{
          position: 'relative',
          // Lift the whole toolbar above the canvas wrapper that
          // follows it in the document — without this the color
          // picker's popup would be painted under the canvas because
          // later siblings naturally win at z=auto.
          zIndex: 20,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          padding: 4,
          marginBottom: 8,
          background: menuBg,
          border: menuBorder,
          borderRadius: 8,
        }}
      >
        <TooltipProvider delayDuration={200} disableHoverableContent>

        {/* Color picker — sits just right of the trash/clear icon at the
            far left. Hidden when `color_menu === 'none'`. */}
        {menuColors && (
          <div
            ref={colorPickerRef}
            style={{
              position: 'absolute',
              left: 40, top: '50%', transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            {menuColors.length <= 3 ? (
              // Few enough colors to show inline — skip the popup.
              menuColors.map((c) => {
                const active = c === color;
                return (
                  <Tooltip key={c}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={c}
                        aria-pressed={active}
                        onClick={() => handleSelectColor(c)}
                        style={{
                          width: 14, height: 14, padding: 0,
                          border: active
                            ? `2px solid ${menuFg}`
                            : `1px solid ${darken(COLOR_MAP[c] ?? c)}`,
                          borderRadius: '50%',
                          background: COLOR_MAP[c] ?? c,
                          cursor: 'pointer',
                          display: 'block',
                        }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>{c}</TooltipContent>
                  </Tooltip>
                );
              })
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label="color"
                      aria-haspopup="true"
                      aria-expanded={colorPickerOpen}
                      onClick={() => setColorPickerOpen((v) => !v)}
                      style={{
                        width: 14, height: 14, padding: 0,
                        border: `1px solid ${darken(parseColor(color))}`,
                        borderRadius: '50%',
                        background: parseColor(color),
                        cursor: 'pointer',
                        display: 'block',
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent>color</TooltipContent>
                </Tooltip>
                {colorPickerOpen && (
                  <div
                    role="menu"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      display: 'grid',
                      gridTemplateColumns: 'repeat(6, 18px)',
                      gap: 4,
                      padding: 6,
                      background: menuBg,
                      border: menuBorder,
                      borderRadius: 6,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                      zIndex: 10,
                    }}
                  >
                    {menuColors.map((c) => {
                      const active = c === color;
                      return (
                        <button
                          key={c}
                          type="button"
                          aria-label={c}
                          title={c}
                          onClick={() => handleSelectColor(c)}
                          style={{
                            width: 14, height: 14, padding: 0,
                            border: active
                              ? `2px solid ${menuFg}`
                              : `1px solid ${darken(COLOR_MAP[c] ?? c)}`,
                            borderRadius: '50%',
                            background: COLOR_MAP[c] ?? c,
                            cursor: 'pointer',
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {menuTools ? (
          menuTools.map((t) => {
            const active = tool === t;
            return (
              <Tooltip key={t}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t}
                    aria-pressed={active}
                    onClick={() => handleSelectTool(t)}
                    style={{
                      width: 28, height: 28,
                      border: 'none', borderRadius: 4,
                      background: active ? 'rgba(127,127,127,0.35)' : 'transparent',
                      color: userColor,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: 0,
                    }}
                  >{TOOL_ICON[t]}</button>
                </TooltipTrigger>
                <TooltipContent>{renderTip(TOOL_TIP[t])}</TooltipContent>
              </Tooltip>
            );
          })
        ) : (
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              color: menuFg, fontFamily: 'system-ui, sans-serif',
              fontSize: 12, lineHeight: 1, height: 28,
            }}
          >
            {tool === 'eraser' ? (
              <>
                drag to erase
                <span style={{ color: userColor, display: 'inline-flex' }}>
                  {TOOL_ICON[tool]}
                </span>
              </>
            ) : tool === 'text' ? (
              <>
                {tapOrClick} to add text
                <span style={{ color: userColor, display: 'inline-flex' }}>
                  {TOOL_ICON[tool]}
                </span>
              </>
            ) : (
              <>
                {hintVerb} to add a
                <span style={{ color: userColor, display: 'inline-flex' }}>
                  {TOOL_ICON[tool]}
                </span>
              </>
            )}
          </span>
        )}

        {/* Far-left: trash icon to clear. Sits left of the color picker. */}
        <div
          style={{
            position: 'absolute',
            left: 4, top: '50%', transform: 'translateY(-50%)',
            display: 'flex', alignItems: 'center',
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="clear"
                onClick={handleClear}
                style={{
                  height: 28, width: 28, padding: 0,
                  border: 'none', borderRadius: 4, background: 'transparent',
                  color: menuFg,
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none"
                     stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round"
                     style={{ marginTop: -3 }}>
                  <path d="M3 6h14" />
                  <path d="M8 6V4h4v2" />
                  <path d="M5 6l1 11h8l1-11" />
                  <path d="M8.5 9v6" strokeWidth={0.8} />
                  <path d="M11.5 9v6" strokeWidth={0.8} />
                </svg>
              </button>
            </TooltipTrigger>
            <TooltipContent>{renderTip('_c clear')}</TooltipContent>
          </Tooltip>
        </div>

        {/* Right-anchored: done. */}
        {show_done && (
          <div
            style={{
              position: 'absolute',
              right: 4, top: '50%', transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center',
            }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="done"
                  onClick={handleDone}
                  style={{
                    height: 28, padding: '0 8px',
                    border: 'none', borderRadius: 4, background: 'transparent',
                    color: menuFg,
                    fontFamily: 'system-ui, sans-serif',
                    fontWeight: 700, fontSize: 11, lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >done</button>
              </TooltipTrigger>
              <TooltipContent>{renderTip('_d done')}</TooltipContent>
            </Tooltip>
          </div>
        )}
        </TooltipProvider>
      </div>
      <div
        ref={wrapperRef}
        className="a-scribble"
        style={{ position: 'relative', isolation: 'isolate', display: 'block', maxWidth: '100%' }}
      >
        <div
          style={{
            position: 'relative',
            zIndex: 0,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {content}
        </div>
        <canvas
          ref={canvasRef}
          width={canvasW}
          height={canvasH}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'absolute', inset: 0,
            zIndex: 1,
            display: 'block',
            width: '100%', height: '100%',
            pointerEvents: 'auto',
            touchAction: 'none',
            userSelect: 'none',
            cursor: shiftDown ? 'grab' : tool === 'eraser' ? 'cell' : tool === 'text' ? 'text' : 'crosshair',
          }}
        />
        {textEditor && textEditorStyle && (
          <textarea
            ref={textEditorRef}
            aria-label="Scribble text"
            value={textEditor.value}
            onChange={(e) => setTextEditor({ ...textEditor, value: e.currentTarget.value })}
            onBlur={(e) => {
              const related = e.relatedTarget as HTMLElement | null;
              if (!related || related === canvasRef.current) {
                requestAnimationFrame(() => {
                  if (textEditorRef.current && document.activeElement !== textEditorRef.current) {
                    commitTextEditor();
                  }
                });
              } else {
                commitTextEditor();
              }
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setTextEditor(null);
              } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                commitTextEditor();
              }
            }}
            placeholder="Type..."
            style={textEditorStyle}
          />
        )}
      </div>
      {commentsEnabled && shapes.some((s) => !!s.label) && (
        <div
          className="a-scribble-comments"
          style={{
            marginTop: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {shapes
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => !!s.label)
            .map(({ s, i }) => {
              const label = s.label as string;
              const text = comments?.[label] ?? '';
              return (
                <div
                  key={`row-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: 'var(--artifact-foreground, inherit)',
                      flexShrink: 0,
                      width: 14,
                      textAlign: 'center',
                      userSelect: 'none',
                    }}
                  >
                    {label.toUpperCase()}
                  </span>
                  <input
                    type="text"
                    value={text}
                    placeholder="comment…"
                    onChange={(e) => {
                      const v = e.target.value;
                      setComments((prev) => {
                        const next = { ...(prev ?? {}), [label]: v };
                        commentsRef.current = next;
                        return next;
                      });
                      dirtyCommentsRef.current = true;
                      scheduleChanged();
                    }}
                    style={{
                      flex: 1,
                      font: '12px ui-sans-serif, system-ui, sans-serif',
                      color: 'var(--artifact-foreground, inherit)',
                      background: 'transparent',
                      border: '1px solid var(--artifact-border, rgba(127,127,127,0.25))',
                      borderRadius: 3,
                      padding: '2px 6px',
                      outline: 'none',
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`delete shape ${label}`}
                    onClick={() => {
                      // Remove the shape and its comment entry.
                      setShapes((prev) => {
                        const next = prev.filter((_, j) => j !== i);
                        shapesRef.current = next;
                        return next;
                      });
                      dirtyShapesRef.current = true;
                      setComments((prev) => {
                        if (!prev) return prev;
                        if (!(label in prev)) return prev;
                        const next = { ...prev };
                        delete next[label];
                        commentsRef.current = next;
                        return next;
                      });
                      dirtyCommentsRef.current = true;
                      scheduleChanged();
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 2,
                      cursor: 'pointer',
                      color: 'var(--artifact-foreground-muted, #888)',
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
